import { parseFirestoreDate } from './parseFirestoreDate';
import { needsRegistrationCompletion } from './registrationFlow';

/** Pre-trial AI consent generations included with every new practice. */
export const FREE_GENERATES_LIMIT = 3;

/** Superadmin "Access Override" — must bypass all subscription paywalls. */
export function hasSuperadminAccessOverride(clinicData) {
  return clinicData?.subscription?.accessOverride === true;
}

/** Paid / trialing / canceled-in-grace access (not free preview). */
export function hasPaidSubscriptionAccess(clinicData) {
  if (!clinicData) return false;
  if (hasSuperadminAccessOverride(clinicData)) return true;

  const sub = clinicData.subscription;
  if (!sub) return false;

  const status = String(sub.status || '').toLowerCase();
  if (status === 'active' || status === 'trialing') return true;

  if (status === 'canceled' || status === 'cancelled' || status === 'cancel_at_period_end') {
    const endDate = parseFirestoreDate(sub.currentPeriodEnd);
    if (endDate && endDate > new Date()) return true;
  }

  return false;
}

/**
 * Whether the clinic may send consents to patients.
 * Paid/trial plans, or a pre-trial practice sending a draft it already has.
 * Generating a new consent requires a trial — see canGenerateConsent.
 * Email verification is enforced separately in the consent UI.
 */
export function canGenerateOrSendConsent(clinicData) {
  if (hasPaidSubscriptionAccess(clinicData)) return true;
  return isEligibleForFreeGenerates(clinicData);
}

export function getFreeGeneratesLimit(clinicData) {
  const n = Number(clinicData?.freeGeneratesLimit);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : FREE_GENERATES_LIMIT;
}

export function getFreeGeneratesUsed(clinicData) {
  const n = Number(clinicData?.freeGeneratesUsed);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Pre-trial / provisional practices only — not canceled paid accounts. */
export function isEligibleForFreeGenerates(clinicData) {
  if (!clinicData || hasPaidSubscriptionAccess(clinicData)) return false;
  if (clinicData.setupStatus === 'provisional') return true;
  const status = String(clinicData?.subscription?.status || '').toLowerCase();
  return status === 'incomplete' || status === 'none' || status === '';
}

/**
 * Remaining free AI generates. `null` when on a paid/trial plan (unlimited via plan).
 */
export function getFreeGeneratesRemaining(clinicData) {
  if (!clinicData) return FREE_GENERATES_LIMIT;
  if (hasPaidSubscriptionAccess(clinicData)) return null;
  if (!isEligibleForFreeGenerates(clinicData)) return 0;
  return Math.max(0, getFreeGeneratesLimit(clinicData) - getFreeGeneratesUsed(clinicData));
}

/** True while the practice is on the free preview allowance (not subscribed). */
export function isOnFreeGeneratePreview(clinicData) {
  return isEligibleForFreeGenerates(clinicData);
}

/**
 * Generate AI consent only after a trial or paid plan has started.
 * Pre-trial practices can fill the form; the first Generate click asks for the trial.
 */
export function canGenerateConsent(clinicData) {
  if (!clinicData) return false;
  return hasPaidSubscriptionAccess(clinicData);
}

/** Account exists but practice / plan setup is not finished. */
export function isSetupIncomplete(userData) {
  return needsRegistrationCompletion(userData);
}

/**
 * High-level access phase for clinic UI.
 * - setup_incomplete: no clinic linked
 * - override: superadmin free access
 * - active: subscribed / trialing
 * - exploring_free: pre-trial practice (can prepare a form; generate requires a trial)
 * - subscription_required: renew needed
 */
export function getClinicAccessPhase(userData, clinicData) {
  if (isSetupIncomplete(userData)) return 'setup_incomplete';
  if (hasSuperadminAccessOverride(clinicData)) return 'override';
  if (hasPaidSubscriptionAccess(clinicData)) return 'active';
  if (isEligibleForFreeGenerates(clinicData)) return 'exploring_free';
  return 'subscription_required';
}

export function getSubscriptionBannerContent(userData, clinicData) {
  const phase = getClinicAccessPhase(userData, clinicData);
  if (phase === 'override' || phase === 'active' || phase === 'exploring_free') return null;

  if (phase === 'setup_incomplete') {
    return null;
  }

  const status = String(clinicData?.subscription?.status || '').toLowerCase();
  const endDate = parseFirestoreDate(clinicData?.subscription?.currentPeriodEnd);
  const inGrace =
    (status === 'canceled' || status === 'cancelled') &&
    endDate &&
    endDate > new Date();

  if (inGrace) {
    return {
      tone: 'amber',
      title: 'Subscription canceled',
      message: `You can still generate consents until ${endDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })}. Renew anytime to keep access.`,
      ctaLabel: 'Manage billing',
      ctaPath: '/clinic/billing',
    };
  }

  return {
    tone: 'amber',
    title: 'Subscription required',
    message:
      'You can add patients and prepare consent forms. Subscribe or renew your plan when you are ready to generate and send.',
    ctaLabel: 'View plans & billing',
    ctaPath: '/clinic/billing',
  };
}

/**
 * @param {'generate' | 'send'} reason
 */
export function getSubscribePromptContent(userData, clinicData, { reason = 'generate' } = {}) {
  const phase = getClinicAccessPhase(userData, clinicData);

  if (hasSuperadminAccessOverride(clinicData)) {
    return null;
  }

  const subStatus = String(clinicData?.subscription?.status || '').toLowerCase();
  const needsFirstTrial =
    phase === 'setup_incomplete' ||
    phase === 'exploring_free' ||
    phase === 'subscription_required' ||
    userData?.registrationStatus === 'pending_plan' ||
    !clinicData?.subscription ||
    subStatus === 'incomplete' ||
    subStatus === 'none' ||
    subStatus === '';

  if (needsFirstTrial) {
    const firstTrial =
      phase === 'setup_incomplete' ||
      phase === 'exploring_free' ||
      userData?.registrationStatus === 'pending_plan' ||
      !clinicData?.subscription ||
      subStatus === 'incomplete' ||
      subStatus === 'none' ||
      subStatus === '';

    // Pre-trial practices can still send a draft they already have. Email verify is separate.
    if (reason === 'send' && isEligibleForFreeGenerates(clinicData)) {
      return null;
    }

    if (reason === 'send' && firstTrial) {
      return {
        title: 'Start your trial to send',
        message:
          'Your AI consent draft is ready. Start a 14-day free trial to email it to the patient — £0 due today, cancel anytime.',
        primaryLabel: 'Start free trial',
        primaryPath: '/auth/select-plan',
        secondaryLabel: 'Keep editing',
        eyebrow: 'Send to patient',
      };
    }

    if (firstTrial) {
      return {
        title: 'Sign up to continue generating this consent',
        message:
          'Your patient and treatment stay on this page. Sign up for the 14-day free trial — £0 today — and generate it as soon as you come back.',
        primaryLabel: 'Sign up',
        primaryPath: '/auth/select-plan',
        secondaryLabel: 'Maybe later',
      };
    }
  }

  return {
    title: 'Upgrade to generate & send consents',
    message:
      'Your draft is ready. Start or renew a plan to generate AI-assisted consents and email them to patients.',
    primaryLabel: 'View plans & billing',
    primaryPath: '/clinic/billing',
    secondaryLabel: 'Keep editing',
  };
}
